/**
 * Erreurs par champ renvoyées par `PATCH /api/cg/:id/review-fields`.
 *
 * Avant, un seul champ invalide faisait échouer tout l'enregistrement avec un
 * « Erreur lors de la sauvegarde des champs revus » global : le 28/09 sur
 * GB-053-TZ, un SIRET de 23 chiffres a emporté la correction du nom du vendeur
 * saisie en même temps, et François a cherché vingt minutes le champ fautif.
 * Le backend enregistre désormais les champs valides et renvoie les autres dans
 * `fieldErrors` ; ce module les rattache aux lignes affichées par FieldEditList.
 */

import type { FieldGroup } from './FieldEditList';

export interface ReviewFieldError {
  fieldKey: string;
  message: string;
}

/** `fieldErrors` d'une réponse d'erreur axios, ou `null` si l'échec n'en porte pas (500, réseau). */
export const extractReviewFieldErrors = (error: unknown): ReviewFieldError[] | null => {
  const fieldErrors = (error as { response?: { data?: { fieldErrors?: unknown } } })?.response?.data?.fieldErrors;
  if (!Array.isArray(fieldErrors) || fieldErrors.length === 0) return null;
  return fieldErrors.filter(
    (e): e is ReviewFieldError => typeof e?.fieldKey === 'string' && typeof e?.message === 'string',
  );
};

interface DisplayedField {
  /** Ligne qui affiche le champ (celle du champ principal pour un champ appairé). */
  rowKey: string;
  label: string;
  groupTitle: string;
}

/** fieldKey → ligne affichée. `heure_vente` est rendu dans la ligne de `date_vente`. */
const indexDisplayedFields = (groups: FieldGroup[]): Map<string, DisplayedField> => {
  const index = new Map<string, DisplayedField>();
  groups.forEach((group) =>
    group.fields.forEach((field) => {
      const displayed = { rowKey: field.fieldKey, label: field.label, groupTitle: group.title };
      index.set(field.fieldKey, displayed);
      if (field.pairedFieldKey) index.set(field.pairedFieldKey, displayed);
    }),
  );
  return index;
};

/**
 * Messages indexés par ligne affichée, au format `invalidFields` de
 * FieldEditList. Deux erreurs sur la même ligne (date + heure) sont réunies.
 */
export const reviewFieldErrorsByRow = (
  errors: Record<string, string>,
  groups: FieldGroup[],
): Record<string, string> => {
  const index = indexDisplayedFields(groups);
  return Object.entries(errors).reduce<Record<string, string>>((acc, [fieldKey, message]) => {
    const rowKey = index.get(fieldKey)?.rowKey ?? fieldKey;
    acc[rowKey] = acc[rowKey] ? `${acc[rowKey]} ${message}` : message;
    return acc;
  }, {});
};

export interface ReviewFieldErrorSummaryItem {
  rowKey: string;
  /** « Vendeur — SIRET » : le groupe lève l'ambiguïté entre deux « Nom ». */
  label: string;
  message: string;
}

/** Liste récapitulative (bandeau), dans l'ordre d'affichage des champs. */
export const summarizeReviewFieldErrors = (
  errors: Record<string, string>,
  groups: FieldGroup[],
): ReviewFieldErrorSummaryItem[] => {
  const index = indexDisplayedFields(groups);
  const displayOrder = groups.flatMap((g) => g.fields.map((f) => f.fieldKey));
  const orderOf = (rowKey: string): number => {
    const position = displayOrder.indexOf(rowKey);
    return position === -1 ? displayOrder.length : position;
  };
  return Object.entries(errors)
    .map(([fieldKey, message]) => {
      const displayed = index.get(fieldKey);
      return {
        rowKey: displayed?.rowKey ?? fieldKey,
        label: displayed ? `${displayed.groupTitle} — ${displayed.label}` : fieldKey,
        message,
      };
    })
    .sort((a, b) => orderOf(a.rowKey) - orderOf(b.rowKey));
};

/** Ancre DOM d'une ligne de champ, pour y amener le relecteur depuis le bandeau. */
export const reviewFieldAnchorId = (fieldKey: string): string => `review-field-${fieldKey}`;
